import { Fragment, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { PageHeader } from '../../components/ui/Card'
import { Input, Select } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { TableWrap } from '../../components/ui/Table'
import { EmptyState } from '../../components/ui/EmptyState'
import { Button } from '../../components/ui/Button'
import { auditApi } from '../../api/audit.api'
import { usersApi } from '../../api/users.api'
import type { AuditLogEntry } from '../../api/types'

const EVENT_VARIANTS: Record<string, 'success' | 'danger' | 'warning' | 'info' | 'neutral'> = {
  created: 'success',
  logged_in: 'success',
  reactivated: 'success',
  setup_completed: 'success',
  updated: 'info',
  roles_changed: 'info',
  members_changed: 'info',
  links_changed: 'info',
  tasks_changed: 'info',
  assignees_changed: 'info',
  labels_changed: 'info',
  status_changed: 'info',
  mapping_set: 'info',
  password_changed: 'info',
  deactivated: 'warning',
  reordered: 'warning',
  deleted: 'danger',
  login_failed: 'danger',
  mapping_removed: 'danger',
  password_reset: 'danger',
}

function variantForEvent(eventType: string) {
  const suffix = eventType.split('.').pop() ?? ''
  return EVENT_VARIANTS[suffix] ?? 'neutral'
}

const OBJECT_TYPES = [
  'User',
  'Role',
  'Project',
  'Task',
  'Label',
  'TaskStatus',
  'Document',
  'RoadmapEntry',
  'ChatChannel',
  'AutomationRule',
  'DiscordChannelMapping',
  'SystemSetting',
]

function ValuesDiff({ label, values }: { label: string; values: unknown }) {
  if (!values || typeof values !== 'object') return null
  const entries = Object.entries(values as Record<string, unknown>)
  if (entries.length === 0) return null
  return (
    <div>
      <div style={{ fontSize: 'var(--text-xs)', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {entries.map(([key, value]) => (
          <div key={key} style={{ fontSize: 'var(--text-sm)' }}>
            <span style={{ color: 'var(--text-muted)' }}>{key}:</span>{' '}
            <span style={{ fontFamily: 'monospace' }}>{typeof value === 'string' ? value : JSON.stringify(value)}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function AuditDetailRow({ entry }: { entry: AuditLogEntry }) {
  const hasDetails = entry.oldValues || entry.newValues || entry.objectId
  if (!hasDetails) return null
  return (
    <tr>
      <td colSpan={5} style={{ background: 'var(--surface-2)', padding: 'var(--space-3)' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-5)' }}>
          {entry.objectId ? (
            <div>
              <div style={{ fontSize: 'var(--text-xs)', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>
                Objekt-ID
              </div>
              <span style={{ fontSize: 'var(--text-sm)', fontFamily: 'monospace' }}>{entry.objectId}</span>
            </div>
          ) : null}
          <ValuesDiff label="Vorher" values={entry.oldValues} />
          <ValuesDiff label="Nachher" values={entry.newValues} />
        </div>
      </td>
    </tr>
  )
}

export function AuditPage() {
  const [userId, setUserId] = useState('')
  const [objectType, setObjectType] = useState('')
  const [eventType, setEventType] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [page, setPage] = useState(1)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const usersQuery = useQuery({ queryKey: ['users', { pageSize: 100 }], queryFn: async () => (await usersApi.list({ pageSize: 100 })).data })

  const auditQuery = useQuery({
    queryKey: ['audit', { userId, objectType, eventType, dateFrom, dateTo, page }],
    queryFn: () =>
      auditApi.list({
        userId: userId || undefined,
        objectType: objectType || undefined,
        eventType: eventType || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        page,
        pageSize: 30,
      }),
  })

  const entries = auditQuery.data?.data ?? []
  const total = auditQuery.data?.meta.total ?? 0
  const hasFilters = Boolean(userId || objectType || eventType || dateFrom || dateTo)

  const resetFilters = () => {
    setUserId('')
    setObjectType('')
    setEventType('')
    setDateFrom('')
    setDateTo('')
    setPage(1)
  }

  return (
    <div>
      <PageHeader title="Audit-Log" subtitle="Wer hat wann was geändert — vollständig nachvollziehbar." />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', marginBottom: 'var(--space-4)', alignItems: 'flex-end' }}>
        <Select style={{ maxWidth: 220 }} value={userId} onChange={(e) => { setUserId(e.target.value); setPage(1) }}>
          <option value="">Alle Benutzer</option>
          {usersQuery.data?.map((u) => (
            <option key={u.id} value={u.id}>
              {u.displayName}
            </option>
          ))}
        </Select>
        <Select style={{ maxWidth: 200 }} value={objectType} onChange={(e) => { setObjectType(e.target.value); setPage(1) }}>
          <option value="">Alle Objekttypen</option>
          {OBJECT_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </Select>
        <Input
          style={{ maxWidth: 240 }}
          placeholder="Event-Typ (z.B. user.updated)…"
          value={eventType}
          onChange={(e) => { setEventType(e.target.value); setPage(1) }}
        />
        <Input type="date" style={{ maxWidth: 160 }} value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1) }} title="Von" />
        <Input type="date" style={{ maxWidth: 160 }} value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1) }} title="Bis" />
        {hasFilters ? (
          <Button size="sm" variant="ghost" onClick={resetFilters}>
            Filter zurücksetzen
          </Button>
        ) : null}
      </div>

      {entries.length === 0 && !auditQuery.isLoading ? (
        <EmptyState mascot title="Keine Einträge" description="Für die aktuelle Filterung liegen keine Audit-Log-Einträge vor." />
      ) : (
        <TableWrap>
          <table>
            <thead>
              <tr>
                <th>Zeit</th>
                <th>Benutzer</th>
                <th>Event</th>
                <th>Objekt</th>
                <th>IP</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => {
                const hasDetails = Boolean(entry.oldValues || entry.newValues || entry.objectId)
                const isExpanded = expandedId === entry.id
                return (
                  <Fragment key={entry.id}>
                    <tr
                      onClick={() => hasDetails && setExpandedId(isExpanded ? null : entry.id)}
                      style={{ cursor: hasDetails ? 'pointer' : 'default' }}
                    >
                      <td style={{ whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>
                        {new Date(entry.createdAt).toLocaleString('de-DE')}
                      </td>
                      <td>{entry.user?.displayName ?? 'System'}</td>
                      <td>
                        <Badge variant={variantForEvent(entry.eventType)}>{entry.eventType}</Badge>
                      </td>
                      <td style={{ color: 'var(--text-secondary)' }}>
                        {entry.objectType ? `${entry.objectType}${entry.objectId ? ` #${entry.objectId.slice(0, 8)}` : ''}` : '—'}
                        {hasDetails ? <span style={{ color: 'var(--text-muted)', marginLeft: 6 }}>{isExpanded ? '▲' : '▼'}</span> : null}
                      </td>
                      <td style={{ color: 'var(--text-muted)' }}>{entry.ip ?? '—'}</td>
                    </tr>
                    {isExpanded ? <AuditDetailRow entry={entry} /> : null}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </TableWrap>
      )}

      {total > 30 ? (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 'var(--space-2)', marginTop: 'var(--space-4)' }}>
          <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Zurück
          </Button>
          <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)', alignSelf: 'center' }}>
            Seite {page} von {Math.ceil(total / 30)}
          </span>
          <Button size="sm" variant="ghost" disabled={page >= Math.ceil(total / 30)} onClick={() => setPage((p) => p + 1)}>
            Weiter
          </Button>
        </div>
      ) : null}
    </div>
  )
}
